import React, { useState } from 'react';
import { useMutation } from '@tanstack/react-query';
import { postStoryAction } from '../../../../lib/api/epics';
import type { StoryAction, StorySummary } from '../../../../../../shared/types/epics';
import { Button, ConfirmDialog as SharedConfirmDialog } from '../../../../components/ui';

// Which actions are available depends on the story's current state.
//   Terminal      COMPLETE | SKIPPED | ABORTED      → no actions
//   Running       *_IN_PROGRESS | *_RUNNING          → Abort only
//   Failed/stuck  *_FAILED | BLOCKED | FORCE_COMPLETE → Retry + Restart + Abort
//   Launchable    LAUNCHABLE | DEFINED                → Launch (= retry) + Abort
//   Idle          everything else (*_COMPLETE phases) → Restart + Abort

const TERMINAL_STATES = new Set(['COMPLETE', 'SKIPPED', 'ABORTED']);

export function storyActionSet(state: string): StoryAction[] {
  if (TERMINAL_STATES.has(state)) return [];
  if (state === 'BLOCKED') return ['unblock', 'retry', 'restart', 'abort'];
  if (state.endsWith('_IN_PROGRESS') || state.endsWith('_RUNNING') || state === 'SMOKE_BROWSER_RUNNING')
    return ['abort'];
  if (state.endsWith('_FAILED') || state === 'FORCE_COMPLETE')
    return ['retry', 'restart', 'abort'];
  if (state === 'LAUNCHABLE' || state === 'DEFINED')
    return ['retry', 'abort'];
  // intermediate _COMPLETE phases — story is between agents, not running
  return ['restart', 'abort'];
}

const STORY_ACTION_LABEL: Record<StoryAction, string> = {
  retry:   'Launch',   // label adapts in the component based on context
  restart: 'Restart',
  abort:   'Abort',
  unblock: 'Unblock',
};

export const StoryActionsBar: React.FC<{
  epicId: number;
  story: StorySummary;
  onSuccess: () => void;
}> = ({ epicId, story, onSuccess }) => {
  const availableActions = storyActionSet(story.state);
  const [confirmAction, setConfirmAction] = useState<StoryAction | null>(null);
  const [toast, setToast] = useState<{ url: string; action: StoryAction } | null>(null);

  const mutation = useMutation({
    mutationFn: (action: StoryAction) =>
      postStoryAction(epicId, story.id, { action }),
    onSuccess: (resp, action) => {
      setToast({ url: resp.commentUrl, action });
      setConfirmAction(null);
      onSuccess();
    },
    onError: () => setConfirmAction(null),
  });

  if (availableActions.length === 0) return null;

  // Use "Launch" label when story hasn't run yet, "Retry" once it has.
  const retryLabel =
    availableActions.includes('retry') && story.sessionCount === 0 ? 'Launch' : 'Retry';

  return (
    <div className="flex items-center gap-1 flex-wrap justify-end">
      {availableActions.map((action) => (
        <Button
          key={action}
          variant={action === 'abort' ? 'danger' : 'secondary'}
          size="sm"
          disabled={mutation.isPending}
          onClick={() => setConfirmAction(action)}
          data-testid={`story-${story.id}-action-${action}`}
        >
          {action === 'retry' ? retryLabel : STORY_ACTION_LABEL[action]}
        </Button>
      ))}

      {toast && (
        <span className="text-[10px] text-success-700 bg-success-50 border border-success-100 rounded px-1.5 py-0.5 whitespace-nowrap">
          /{toast.action} ·{' '}
          <a href={toast.url} target="_blank" rel="noreferrer" className="underline">
            view ↗
          </a>
        </span>
      )}
      {mutation.isError && (
        <span
          role="alert"
          className="text-[10px] text-danger-700 bg-danger-50 border border-danger-100 rounded px-1.5 py-0.5"
        >
          {(mutation.error as Error).message}
        </span>
      )}

      {confirmAction && (
        <StoryConfirmDialog
          action={confirmAction}
          storyId={story.id}
          retryLabel={retryLabel}
          pending={mutation.isPending}
          onConfirm={() => mutation.mutate(confirmAction)}
          onCancel={() => setConfirmAction(null)}
        />
      )}
    </div>
  );
};

const StoryConfirmDialog: React.FC<{
  action: StoryAction;
  storyId: string;
  retryLabel: string;
  pending: boolean;
  onConfirm: () => void;
  onCancel: () => void;
}> = ({ action, storyId, retryLabel, pending, onConfirm, onCancel }) => {
  const label =
    action === 'retry' ? retryLabel : STORY_ACTION_LABEL[action];
  const isDestructive = action === 'abort';
  return (
    <SharedConfirmDialog
      open
      title={`${label} story ${storyId}?`}
      description={
        <>
          Posts <code className="font-mono">/{action}</code> as a comment on the story&apos;s
          GitHub issue. The orchestrator picks it up on the next webhook tick.
        </>
      }
      confirmLabel={pending ? 'Sending…' : `Yes, ${label.toLowerCase()}`}
      tone={isDestructive ? 'danger' : 'primary'}
      pending={pending}
      onConfirm={onConfirm}
      onCancel={onCancel}
      testId={`story-confirm-${action}`}
    />
  );
};
